import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Available Bryntum themes
export const BRYNTUM_THEMES = [
  { id: 'stockholm-light', name: 'Stockholm Light', isDark: false },
  { id: 'stockholm-dark', name: 'Stockholm Dark', isDark: true },
  { id: 'material3-light', name: 'Material 3 Light', isDark: false },
  { id: 'material3-dark', name: 'Material 3 Dark', isDark: true },
  { id: 'fluent2-light', name: 'Fluent 2 Light', isDark: false },
  { id: 'fluent2-dark', name: 'Fluent 2 Dark', isDark: true },
  { id: 'svalbard-light', name: 'Svalbard Light', isDark: false },
  { id: 'svalbard-dark', name: 'Svalbard Dark', isDark: true },
  { id: 'visby-light', name: 'Visby Light', isDark: false },
  { id: 'visby-dark', name: 'Visby Dark', isDark: true },
  { id: 'high-contrast-light', name: 'High Contrast Light', isDark: false },
  { id: 'high-contrast-dark', name: 'High Contrast Dark', isDark: true },
] as const;

export type BryntumThemeId = typeof BRYNTUM_THEMES[number]['id'];

interface BryntumThemeContextType {
  currentTheme: BryntumThemeId;
  setTheme: (themeId: BryntumThemeId) => void;
  isThemeLoaded: boolean;
  isDarkTheme: boolean;
}

const BryntumThemeContext = createContext<BryntumThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'bryntum-theme';
const DEFAULT_THEME: BryntumThemeId = 'stockholm-light';

// Keep track of loaded theme stylesheets
let currentStylesheet: HTMLLinkElement | null = null;

export function BryntumThemeProvider({ children }: { children: React.ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<BryntumThemeId>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && BRYNTUM_THEMES.some(t => String(t.id) === String(saved))) {
        return saved as BryntumThemeId;
      }
    }
    return DEFAULT_THEME;
  });
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  const loadThemeCSS = useCallback((themeId: BryntumThemeId) => {
    setIsThemeLoaded(false);

    // Remove existing Bryntum theme stylesheet if any
    if (currentStylesheet) {
      currentStylesheet.remove();
      currentStylesheet = null;
    }

    // Also remove any existing Bryntum theme links (cleanup)
    document.querySelectorAll('link[data-bryntum-theme]').forEach(el => el.remove());

    // Create new stylesheet link - load from public/bryntum-themes folder
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `/bryntum-themes/${themeId}.css`;
    link.setAttribute('data-bryntum-theme', themeId);

    link.onload = () => {
      setIsThemeLoaded(true);
    };

    link.onerror = () => {
      console.error(`Failed to load Bryntum theme: ${themeId}`);
      setIsThemeLoaded(true); // Continue anyway to not block the UI
    };

    document.head.appendChild(link);
    currentStylesheet = link;
  }, []);

  const setTheme = useCallback((themeId: BryntumThemeId) => {
    setCurrentTheme(themeId);
    localStorage.setItem(STORAGE_KEY, themeId);
    loadThemeCSS(themeId);

    // Update document class for dark/light mode
    const theme = BRYNTUM_THEMES.find(t => String(t.id) === String(themeId));
    if (theme) {
      if (theme.isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [loadThemeCSS]);

  // Load initial theme on mount
  useEffect(() => {
    loadThemeCSS(currentTheme);
    
    // Set initial dark mode class
    const theme = BRYNTUM_THEMES.find(t => String(t.id) === String(currentTheme));
    if (theme?.isDark) {
      document.documentElement.classList.add('dark');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isDarkTheme = BRYNTUM_THEMES.find(t => String(t.id) === String(currentTheme))?.isDark ?? false;

  return (
    <BryntumThemeContext.Provider value={{ currentTheme, setTheme, isThemeLoaded, isDarkTheme }}>
      {children}
    </BryntumThemeContext.Provider>
  );
}

export function useBryntumTheme() {
  const context = useContext(BryntumThemeContext);
  if (context === undefined) {
    throw new Error('useBryntumTheme must be used within a BryntumThemeProvider');
  }
  return context;
}
